<template>
	<VApp>
		<Component
			:is="layouts[layout]"
			:options="layoutOptions"
		>
			<RouterView />
		</Component>
		<AppToasts />
		<ConfirmationDialog />
		<TermsOfUseDialog />
		<MediaPreviewOptionalTaggingDialog
			v-if="isMediaPreviewDialogRendered"
			v-model="mediaPreviewDialogState.isOpen"
			v-model:index="mediaPreviewDialogState.currentIndex"
			:actions-menu-items="mediaPreviewDialogState.actionsMenuItems"
			:disable-tagging="mediaPreviewDialogState.disableTagging"
			:download="mediaPreviewDialogState.download"
			:enable-increment="mediaPreviewDialogState.enableIncrement"
			:media="mediaPreviewDialogState.media"
			@view-details="mediaPreviewDialogState.onViewDetails"
		/>
	</VApp>
</template>

<script setup lang="ts">
import { onBeforeMount, defineAsyncComponent, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { getLocalStorageItem } from '@/utils/localStorage';
import { initGainsight } from '@/plugins/gainsight';
import TermsOfUseDialog from '@/components/TermsOfUseDialog.vue';
import { useLayouts } from '@/composables/useLayouts';
import { useLocales } from '@/composables/useLocales';
import { useMediaPreviewDialog } from './composables/useMediaPreviewDialog';
import { getRouteTitle } from './composables/setTitleTag';
import AppToasts from '@/components/AppToasts.vue';
import { ConfirmationDialog } from '@teamworksdev/vuetify';
import { configureToasts } from '@/composables/useToasts';
import { useLDClient } from 'launchdarkly-vue-client-sdk';
import { buildUserContext } from './plugins/launchdarkly';
import { useUserStore } from './stores/userStore';

const { t } = useI18n();

// MEDIA PREVIEW
const MediaPreviewOptionalTaggingDialog = defineAsyncComponent(() => import('@/components/MediaPreviewOptionalTaggingDialog.vue'));
const {
	state: mediaPreviewDialogState,
	isRendered: isMediaPreviewDialogRendered,
} = useMediaPreviewDialog();


const { layout, layouts, layoutOptions } = useLayouts();
const { setLocale } = useLocales();
const routeTitle = getRouteTitle();

useHead({
	templateParams: {
		site: { name: 'Influencer' },
		separator: '|',
	},
	titleTemplate: '%s %separator %site.name',
	title: () => routeTitle.value,
});

// Init Vue LD
const userStore = useUserStore();
const ldClient = useLDClient();

watch(() => userStore.profile, async () => {
	const { user, profile } = userStore;
	if (user && profile) {
		const context = buildUserContext(user, profile);
		await ldClient.identify(context);
	}
}, { immediate: true });

onBeforeMount(() => {
	setLocale(getLocalStorageItem<string>('inflcr-app-locale') || window.navigator.language);
	initGainsight();
	configureToasts({
		maxToasts: 3,
		genericError: {
			title: t('inflcrErrorCodes.default.title'),
			description: t('inflcrErrorCodes.default.message'),
		},
	});
});
</script>
